import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Swords, Clock, Trophy, Skull, Zap, RefreshCw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LiveBattleViewer({ agentId, manaverseName }) {
  const [battleData, setBattleData] = useState(null);
  const [globalBattles, setGlobalBattles] = useState([]);
  const [countdown, setCountdown] = useState(60);
  const [showingBattle, setShowingBattle] = useState(false);
  const [currentLogIndex, setCurrentLogIndex] = useState(0);
  const [lastBattleId, setLastBattleId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [displayBattle, setDisplayBattle] = useState(null);

  const loadBattleStatus = useCallback(async () => {
    if (!agentId) return;
    
    try {
      // Load user's battle status
      const response = await axios.get(`${API}/battle/live/${agentId}`);
      const newData = response.data;
      setBattleData(newData);
      
      // Check if this is a new battle for the user
      if (newData?.battle?.id && newData.battle.id !== lastBattleId) {
        setLastBattleId(newData.battle.id);
        if (newData.in_battle || newData.battle.seconds_ago < 60) {
          setDisplayBattle({ ...newData.battle, isUserBattle: true });
          setShowingBattle(true);
          setCurrentLogIndex(0);
        }
      }
    } catch (error) {
      console.error("Failed to load battle status:", error);
    }
  }, [agentId, lastBattleId]);

  const loadGlobalBattles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/activity/live`);
      const battles = response.data.recent_battles || [];
      setGlobalBattles(battles);
      
      // If user has no recent battle, show the latest global battle
      if (battles.length > 0 && (!battleData?.in_battle || !displayBattle)) {
        const latestBattle = battles[0];
        if (latestBattle.id !== lastBattleId) {
          // Check if it's within 60 seconds
          const battleTime = new Date(latestBattle.created_at);
          const now = new Date();
          const secondsAgo = Math.floor((now - battleTime) / 1000);
          
          if (secondsAgo < 60) {
            setLastBattleId(latestBattle.id);
            setDisplayBattle({
              ...latestBattle,
              isUserBattle: latestBattle.challenger_id === agentId || latestBattle.opponent_id === agentId,
              seconds_ago: secondsAgo
            });
            setShowingBattle(true);
            setCurrentLogIndex(0);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load global battles:", error);
    }
  }, [agentId, battleData, displayBattle, lastBattleId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([loadBattleStatus(), loadGlobalBattles()]);
    setIsRefreshing(false);
  };

  useEffect(() => {
    loadBattleStatus();
    loadGlobalBattles();
    
    // Poll every 3 seconds
    const interval = setInterval(() => {
      loadBattleStatus();
      loadGlobalBattles();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [loadBattleStatus, loadGlobalBattles]);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const seconds = now.getSeconds();
      const remaining = 60 - seconds;
      setCountdown(remaining === 60 ? 0 : remaining);
      
      // Auto-refresh near battle time
      if (remaining <= 3 || remaining >= 57) {
        loadGlobalBattles();
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [loadGlobalBattles]);

  // Animate battle logs
  useEffect(() => {
    if (showingBattle && displayBattle?.logs?.length > 0) {
      const logTimer = setInterval(() => {
        setCurrentLogIndex(prev => {
          if (prev >= displayBattle.logs.length - 1) {
            clearInterval(logTimer);
            setTimeout(() => {
              setShowingBattle(false);
              setDisplayBattle(null);
            }, 5000);
            return prev;
          }
          return prev + 1;
        });
      }, 500);
      
      return () => clearInterval(logTimer);
    }
  }, [showingBattle, displayBattle?.id]);

  const currentLog = displayBattle?.logs?.[currentLogIndex];
  const isUserBattle = displayBattle?.isUserBattle;

  return (
    <Card data-testid="live-battle-viewer" className="glass-card border-white/10 overflow-hidden">
      <CardHeader className="pb-2 px-3 sm:px-6">
        <CardTitle className="font-rajdhani text-lg sm:text-xl text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Swords className="text-yellow-400 w-5 h-5" />
            <span className="hidden sm:inline">Battle Arena</span>
            <span className="sm:hidden">Arena</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleRefresh}
              disabled={isRefreshing}
              size="sm"
              variant="ghost"
              className="p-1 h-8 w-8"
            >
              <RefreshCw className={`w-4 h-4 text-cyan-400 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <div className="flex items-center gap-1 text-sm">
              <Clock className="text-cyan-400 w-4 h-4" />
              <span className="font-mono text-cyan-400 text-base sm:text-lg font-bold">
                {countdown}s
              </span>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 sm:px-6 pb-4">
        {/* Countdown Progress Bar */}
        <div className="mb-3">
          <Progress 
            value={(60 - countdown) / 60 * 100} 
            className="h-2 sm:h-3"
          />
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1 text-center">
            Battles every 60s
          </p>
        </div>

        <AnimatePresence mode="wait">
          {showingBattle && displayBattle ? (
            <motion.div
              key="battle"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="space-y-3"
            >
              {/* Battle Alert */}
              <motion.div
                animate={{ 
                  boxShadow: isUserBattle 
                    ? ["0 0 10px rgba(255,215,0,0.3)", "0 0 20px rgba(255,215,0,0.5)", "0 0 10px rgba(255,215,0,0.3)"]
                    : ["0 0 10px rgba(0,240,255,0.2)", "0 0 15px rgba(0,240,255,0.3)", "0 0 10px rgba(0,240,255,0.2)"]
                }}
                transition={{ duration: 1, repeat: Infinity }}
                className={`rounded-lg p-3 border ${isUserBattle ? 'bg-gradient-to-r from-yellow-900/40 to-orange-900/40 border-yellow-500/50' : 'bg-gradient-to-r from-cyan-900/30 to-purple-900/30 border-cyan-500/30'}`}
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  {isUserBattle ? (
                    <Swords className="text-yellow-400 animate-pulse w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <Eye className="text-cyan-400 w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                  <span className={`font-unbounded text-sm sm:text-base font-bold ${isUserBattle ? 'text-yellow-400' : 'text-cyan-400'}`}>
                    {isUserBattle ? 'YOUR BATTLE!' : 'LIVE BATTLE'}
                  </span>
                </div>
                <div className="text-center text-xs sm:text-sm text-slate-300">
                  <span className="text-cyan-400 font-bold">{displayBattle.challenger_name?.slice(0, 12)}</span>
                  <span className="mx-1 text-slate-500">vs</span>
                  <span className="text-purple-400 font-bold">{displayBattle.opponent_name?.slice(0, 12)}</span>
                </div>
              </motion.div>

              {/* Battle Cards */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded-lg ${displayBattle.challenger_id === agentId ? 'bg-cyan-900/30 border border-cyan-500/50' : 'bg-white/5'}`}>
                  <div className="text-center">
                    <p className="font-rajdhani text-[10px] sm:text-xs text-slate-400">Challenger</p>
                    <p className="font-unbounded text-xs sm:text-sm text-white truncate">{displayBattle.challenger_name}</p>
                    {displayBattle.challenger_id === agentId && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full">YOU</span>
                    )}
                  </div>
                </div>
                <div className={`p-2 rounded-lg ${displayBattle.opponent_id === agentId ? 'bg-purple-900/30 border border-purple-500/50' : 'bg-white/5'}`}>
                  <div className="text-center">
                    <p className="font-rajdhani text-[10px] sm:text-xs text-slate-400">Opponent</p>
                    <p className="font-unbounded text-xs sm:text-sm text-white truncate">{displayBattle.opponent_name}</p>
                    {displayBattle.opponent_id === agentId && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">YOU</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Live Battle Log */}
              {currentLog && (
                <motion.div
                  key={currentLogIndex}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-black/30 rounded-lg p-2 sm:p-3 border border-white/10"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <Zap className="text-yellow-400 w-3 h-3" />
                    <span className="font-mono text-[10px] text-slate-500">Turn {currentLog.turn}</span>
                  </div>
                  <p className="font-inter text-xs sm:text-sm text-slate-300">
                    <span className="text-cyan-400 font-bold">{currentLog.attacker?.slice(0, 10)}</span>
                    {' → '}
                    <span className="text-yellow-400">{currentLog.move}</span>
                    {' '}
                    <span className="text-red-400 font-bold">-{currentLog.damage}</span>
                  </p>
                </motion.div>
              )}

              {/* Battle Result */}
              {displayBattle.winner && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`text-center p-3 rounded-lg ${
                    displayBattle.winner === agentId 
                      ? 'bg-green-900/30 border border-green-500/50' 
                      : displayBattle.challenger_id === agentId || displayBattle.opponent_id === agentId
                        ? 'bg-red-900/30 border border-red-500/50'
                        : 'bg-purple-900/30 border border-purple-500/50'
                  }`}
                >
                  {displayBattle.winner === agentId ? (
                    <div className="flex items-center justify-center gap-2">
                      <Trophy className="text-yellow-400 w-5 h-5" />
                      <span className="font-unbounded text-lg font-bold text-green-400">VICTORY!</span>
                    </div>
                  ) : displayBattle.challenger_id === agentId || displayBattle.opponent_id === agentId ? (
                    <div className="flex items-center justify-center gap-2">
                      <Skull className="text-red-400 w-5 h-5" />
                      <span className="font-unbounded text-lg font-bold text-red-400">DEFEAT</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Trophy className="text-purple-400 w-5 h-5" />
                      <span className="font-unbounded text-sm font-bold text-purple-400">
                        {displayBattle.winner === displayBattle.challenger_id 
                          ? displayBattle.challenger_name 
                          : displayBattle.opponent_name} wins!
                      </span>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="waiting"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-4"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-3"
              >
                <Swords size={36} className="text-slate-600" />
              </motion.div>
              <p className="font-rajdhani text-sm sm:text-base text-slate-400">
                {manaverseName?.slice(0, 15)} preparing...
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Next round: <span className="text-cyan-400 font-mono font-bold">{countdown}s</span>
              </p>
              
              {/* Last Global Battle */}
              {globalBattles.length > 0 && (
                <div className="mt-3 p-2 bg-white/5 rounded-lg text-left">
                  <p className="text-[10px] text-slate-500 mb-1">Latest Arena Battle:</p>
                  <p className="text-xs text-slate-300">
                    <span className="text-cyan-400">{globalBattles[0].challenger_name?.slice(0, 10)}</span>
                    <span className="text-slate-500"> vs </span>
                    <span className="text-purple-400">{globalBattles[0].opponent_name?.slice(0, 10)}</span>
                  </p>
                  {globalBattles[0].winner && (
                    <p className="text-[10px] text-green-400 mt-1">
                      Winner: {globalBattles[0].winner === globalBattles[0].challenger_id 
                        ? globalBattles[0].challenger_name 
                        : globalBattles[0].opponent_name}
                    </p>
                  )}
                </div>
              )}
              
              {/* User's Last Battle */}
              {battleData?.battle && (
                <div className="mt-2 p-2 bg-white/5 rounded-lg">
                  <p className="text-[10px] text-slate-500 mb-1">Your Last Battle ({battleData.battle.seconds_ago}s ago)</p>
                  <p className="text-xs text-slate-300">
                    {battleData.battle.you_won ? (
                      <span className="text-green-400">✓ Won vs {battleData.battle.your_role === 'challenger' ? battleData.battle.opponent_name?.slice(0, 10) : battleData.battle.challenger_name?.slice(0, 10)}</span>
                    ) : battleData.battle.you_won === false ? (
                      <span className="text-red-400">✗ Lost vs {battleData.battle.your_role === 'challenger' ? battleData.battle.opponent_name?.slice(0, 10) : battleData.battle.challenger_name?.slice(0, 10)}</span>
                    ) : (
                      <span className="text-slate-400">Pending...</span>
                    )}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
